import { Icon, type IconName } from './Icon';

/**
 * Graceful no-image treatment (R17 Q1 — "three don't display images, just random numbers").
 *
 * Field evidence (dev-79 fifth on-kiosk session, turns 22–24): products without a valid image
 * rendered the raw SKU string where the hero image belongs — a bare code reads as "random numbers"
 * to the visitor. This is the shared, editorial fallback used everywhere an image can be absent:
 * the browse tiles (ProductCards), the S6 review rows (OrderReview), and the S7 recap rows
 * (CloseScene). It NEVER shows the raw SKU as the hero — the piece's NAME, set in the display serif,
 * carries the well, with a quiet line icon + hairline framing so an imageless piece still reads as a
 * catalogued object, not a broken tile.
 *
 * Two sizes:
 *   - 'tile'  → the browse/showcase image well (aspect box) — name centred, larger serif.
 *   - 'thumb' → the small square thumbnail in the review/recap rows — icon + a compact serif name
 *               (truncated to a line so the row height stays even). The row already prints the full
 *               name beside the thumb, so the thumb name is a quiet echo, not the primary label.
 *
 * Design language (R27 branded treatment, §8): the category line-glyph sits on a **warm-metallic
 * weave** (`.brand-weave` — a soft brass-tint radial + faint brushed hatch, both themes) with the
 * piece name in the display serif and a quiet category label, so an imageless catalogued piece reads
 * as intentional (a plinth in the gallery), not a broken grey grid. Tokens only, no emoji, no raw SKU.
 * Reduced-motion safe (no motion of its own). Decorative — the row/card already labels the piece for
 * assistive tech, so the fill is aria-hidden.
 */

type Props = {
  /** The piece's display name — the ONLY name text shown (never the SKU). */
  name?: string | null;
  /** Optional category-aware line icon; defaults to the neutral `image` glyph. */
  icon?: IconName;
  /** Optional category display label — a quiet caption under the glyph (branded treatment, R27). */
  category?: string | null;
  /** 'tile' = large image well (browse/showcase); 'thumb' = small row thumbnail. */
  variant?: 'tile' | 'thumb';
};

export function NoImageFill({ name, icon = 'image', category, variant = 'tile' }: Props) {
  // Never fall back to a SKU / raw code here — an absent name degrades to a quiet, wordless well
  // (the icon carries it) rather than surfacing a bare identifier as the hero.
  const label = typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
  const cat = typeof category === 'string' && category.trim() !== '' ? category.trim() : null;

  if (variant === 'thumb') {
    return (
      <div
        aria-hidden="true"
        data-testid="no-image-fill"
        data-variant="thumb"
        className="brand-weave flex h-full w-full flex-col items-center justify-center gap-0.5 px-1 text-center ring-1 ring-line"
      >
        <Icon name={icon} size={15} className="text-brass/80" />
        {label ? (
          <span className="line-clamp-1 font-display text-[8px] leading-tight text-subtle">
            {label}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      data-testid="no-image-fill"
      data-variant="tile"
      className="brand-weave flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center ring-1 ring-line"
    >
      <Icon name={icon} size={26} className="text-brass/80" />
      {label ? (
        <span className="line-clamp-2 max-w-full font-display text-sm leading-tight text-ink/80">
          {label}
        </span>
      ) : (
        <span className="text-[10px] uppercase tracking-wide text-faint">image coming soon</span>
      )}
      {cat ? (
        <span className="text-[9px] uppercase tracking-[0.14em] text-faint">{cat}</span>
      ) : null}
    </div>
  );
}
